import React, { memo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { BringgInput } from '@bringg/react-components';
import { TypographyColor, TypographyHelper } from '@bringg-frontend/ui';

import styles from './max-task-capacity-input.module.scss';

// Non-negative integers only — matches the backend's json-schema (max_task_capacity: integer, minimum 0).
const VALID_CAPACITY_PATTERN = /^\d+$/;

interface Props {
	maxTaskCapacity: number | null;
	onMaxTaskCapacityChange: (maxTaskCapacity: number | null) => void;
}

const MaxTaskCapacityInput = ({ maxTaskCapacity, onMaxTaskCapacityChange }: Props) => {
	const { t } = useTranslation();
	// Free-typed text, not the committed value — lets an in-progress invalid value (e.g. "-7", "e",
	// "0.5") stay visible on screen instead of being silently stripped or discarded mid-keystroke.
	const [rawValue, setRawValue] = useState(maxTaskCapacity != null ? String(maxTaskCapacity) : '');
	const [touched, setTouched] = useState(false);

	const trimmedValue = rawValue.trim();
	const isEmpty = trimmedValue === '';
	const isInvalidFormat = !isEmpty && !VALID_CAPACITY_PATTERN.test(trimmedValue);
	const showRequiredError = touched && isEmpty;
	const hasError = isInvalidFormat || showRequiredError;

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setRawValue(value);

		const trimmed = value.trim();
		onMaxTaskCapacityChange(VALID_CAPACITY_PATTERN.test(trimmed) ? Number(trimmed) : null);
	};

	const errorMessage = isInvalidFormat
		? t('PLANNED_DELIVERY_WINDOWS_MODAL.CAPACITY_INVALID_ERROR')
		: showRequiredError
		? t('PLANNED_DELIVERY_WINDOWS_MODAL.CAPACITY_REQUIRED_ERROR')
		: undefined;

	return (
		<>
			<BringgInput
				data-test-id="max-task-capacity-input"
				className={classNames(styles.maxTaskCapacityInput, hasError && styles.error)}
				status={hasError ? 'error' : undefined}
				value={rawValue}
				onChange={handleChange}
				onBlur={() => setTouched(true)}
				placeholder={t('PLANNED_DELIVERY_WINDOWS_MODAL.CAPACITY_PLACEHOLDER')}
				suffix={t('PLANNED_DELIVERY_WINDOWS_MODAL.CAPACITY_UNITS')}
			/>
			<TypographyHelper className={styles.helperText} color={errorMessage ? TypographyColor.Danger : undefined}>
				{errorMessage ?? t('PLANNED_DELIVERY_WINDOWS_MODAL.CAPACITY_HELPER')}
			</TypographyHelper>
		</>
	);
};

export default memo(MaxTaskCapacityInput);
